import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Typography,
    Avatar,
    Switch,
    GlobalStyles,
    useTheme,
    useMediaQuery,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Select,
    MenuItem,
    IconButton,
    CircularProgress,
    Tooltip
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import UndoIcon from "@mui/icons-material/Undo";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import {
    fetchCustomers,
    toggleCustomerStatus,
    deleteCustomer
} from "../../Redux/Reducers/CustomersReducer";
import { fetchSubscriptions, assignSubscription, revokeSubscription } from "../../Redux/Reducers/SubscriptionsReducer";
import { formatUTCDateForDataGrid } from "../../utils/dateUtils";
import { getFullUrl } from "../../utils/globalUtils";
import usePermissions from "../../hooks/usePermissions";

const MODULE_KEY = "customer";

function CustomerList() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { hasAction } = usePermissions();

    const { customers = [], loading, pagination } = useSelector((state) => state.customers || {});
    const { subscriptions = [] } = useSelector((state) => state.subscriptions || {});
    const [searchQuery, setSearchQuery] = useState("");
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [deleteSelectedId, setDeleteSelectedId] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        id: null,
        newStatus: null,
    });

    const [assignDialog, setAssignDialog] = useState({
        open: false,
        customerId: null,
        customerName: "",
        planId: "",
    });

    const [revokePermissionDialog, setRevokePermissionDialog] = useState({
        open: false,
        customerId: null
    });

    const [assigning, setAssigning] = useState(false);
    const [revoking, setRevoking] = useState(false);
    const [statusUpdating, setStatusUpdating] = useState(false);

    // For Table Thead Width
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isLarge = useMediaQuery(theme.breakpoints.up('md'));

    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        dispatch(fetchCustomers({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            search: debouncedSearch,
        }));
    }, [paginationModel, debouncedSearch]);

    // Reset assign subscription form when modal opens
    useEffect(() => {
        if (assignDialog.open) {
            assignDialog.planId = "";
            dispatch(fetchSubscriptions({ fetchType: "dropdown", limit: 10000 }));
        }
    }, [assignDialog.open]);

    const handleAssignSubscription = async () => {
        setAssigning(true);
        try {
            await dispatch(
                assignSubscription({
                    customerId: assignDialog.customerId,
                    planId: assignDialog.planId,
                    source: "MANUAL",
                })
            ).unwrap();

            toast.success(t('customers.messages.subscription_assigned_success', "Subscription assigned successfully"));

            setAssignDialog({ open: false, customerId: null, planId: "" });

            // Refresh list
            dispatch(fetchCustomers({
                page: paginationModel.page + 1,
                limit: paginationModel.pageSize,
                search: debouncedSearch,
            }));

        } catch (err) {
            toast.error(err || "Failed to assign subscription");
        } finally {
            setAssigning(false);
        }
    };

    const handleRevokeSubscription = async () => {
        setRevoking(true);
        try {
            await dispatch(
                revokeSubscription({
                    customerId: revokePermissionDialog.customerId,
                })
            ).unwrap();

            toast.success(t('customers.messages.subscription_revoked_success', "Subscription revoked successfully"));

            setRevokePermissionDialog({ open: false, customerId: null });

            // Refresh list
            dispatch(fetchCustomers({
                page: paginationModel.page + 1,
                limit: paginationModel.pageSize,
                search: debouncedSearch,
            }));

        } catch (err) {
            toast.error(err || "Failed to revoke subscription");
        } finally {
            setRevoking(false);
        }
    };

    const handleConfirmStatusUpdate = async () => {
        setStatusUpdating(true);
        try {
            await dispatch(
                toggleCustomerStatus({
                    id: confirmDialog.id,
                    isActive: confirmDialog.newStatus,
                })
            ).unwrap();

            toast.success(t("customers.messages.status_update_success", "Status updated successfully"));
        } catch (err) {
            toast.error(
                err?.message ||
                t("customers.messages.status_update_error", "Failed to update status")
            );
        } finally {
            setStatusUpdating(false);
            setConfirmDialog({ open: false, id: null, newStatus: null });
        }
    };

    const handleDeleteClick = (id) => {
        setDeleteSelectedId(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            await dispatch(deleteCustomer({ id: deleteSelectedId })).unwrap();
            toast.success(t('customers.messages.delete_success', "Customer deleted successfully"));
            dispatch(
                fetchCustomers({
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: debouncedSearch,
                })
            );
        } catch (err) {
            toast.error(t('customers.messages.delete_failed', "Failed to delete customer"));
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
            setDeleteSelectedId(null);
        }
    };

    const columns = [
        {
            field: 'sno',
            headerName: t('customers.fields.sno', 'S.No'),
            width: 40,
            sortable: false,
            renderCell: (params) => {
                const rowsInCurrentPage = params.api.getRowsCount();
                const allRows = params.api.getAllRowIds();
                const currentRowIndex = allRows.indexOf(params.id);
                const visibleRowIndex = currentRowIndex % paginationModel.pageSize;
                return paginationModel.page * paginationModel.pageSize + visibleRowIndex + 1;
            }
        },
        {
            field: "profileImage",
            headerName: "",
            width: 60,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                return (<Avatar variant="square" src={params.value} sx={{ width: 32, height: 32, borderRadius: '5px' }} />);
            }
        },
        {
            field: "name",
            headerName: t('customers.fields.name', 'Name'),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
        },
        {
            field: "email",
            headerName: t('customers.fields.email', 'Email'),
            ...(isMobile && { width: 200 }),
            ...(isLarge && { flex: 1 }),
        },
        {
            field: "phone",
            headerName: t('customers.fields.phone', 'Phone'),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
        },
        {
            field: "active_plan",
            headerName: t('customers.fields.active_plan', "Active Plan"),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            sortable: false,
            renderCell: (params) => {
                return (
                    (params.value ? (
                        <Box sx={{ fontSize: "12px" }}>
                            <Typography sx={{ fontSize: "12px" }}>{`${params.value?.plan?.name}/${params.value?.plan?.durationDays} Days`}</Typography>
                            <Typography sx={{ fontSize: "12px" }}>Starts At: {formatUTCDateForDataGrid(params.value?.startsAt)}</Typography>
                            <Typography sx={{ fontSize: "12px" }}>Ends At: {formatUTCDateForDataGrid(params.value?.endsAt)}</Typography>
                        </Box>
                    ) : "N/A")
                );
            }
        },
        {
            field: "createdAt",
            headerName: t('customers.fields.createdAt', "Registered At"),
            ...(isMobile && { width: 180 }),
            ...(isLarge && { width: 200 }),
            valueFormatter: (value) => {
                return formatUTCDateForDataGrid(value);
            }
        },
        {
            field: "isActive",
            headerName: t('customers.fields.status', "Status"),
            ...(isMobile && { width: 100 }),
            ...(isLarge && { width: 100 }),
            renderCell: (params) => {
                return (
                    <Switch
                        checked={Boolean(params.value)}
                        onChange={(e) =>
                            setConfirmDialog({
                                open: true,
                                id: params.row.id,
                                newStatus: e.target.checked,
                            })
                        }
                        color="success"
                    />
                );
            }
        },
        {
            field: "actions",
            headerName: t("actions", "Actions"),
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Box sx={{ display: 'flex', gap: '6px', }}>
                        {hasAction(MODULE_KEY, 'delete') && (
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(params.row.id)}
                                sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
                                    <path d="M7.21951 1.17073C6.4558 1.17073 5.80457 1.65856 5.56322 2.34141C5.45548 2.64622 5.12105 2.80599 4.81623 2.69825C4.51142 2.59051 4.35166 2.25608 4.4594 1.95127C4.86091 0.81529 5.94427 0 7.21951 0C8.49483 0 9.57815 0.81529 9.97971 1.95127C10.0874 2.25608 9.92765 2.59051 9.62287 2.69825C9.31801 2.80599 8.98357 2.64622 8.87586 2.34141C8.63454 1.65856 7.98322 1.17073 7.21951 1.17073Z" fill="white" />
                                    <path d="M0 3.70744C0 3.38415 0.26208 3.12207 0.585366 3.12207H13.8537C14.177 3.12207 14.4391 3.38415 14.4391 3.70744C14.4391 4.03072 14.177 4.2928 13.8537 4.2928H0.585366C0.26208 4.2928 0 4.03072 0 3.70744Z" fill="white" />
                                    <path d="M2.47023 5.61969C2.44873 5.29712 2.1698 5.05306 1.84723 5.07456C1.52466 5.09606 1.28059 5.37499 1.3021 5.69757L1.66382 11.1233C1.73054 12.1245 1.78444 12.9332 1.91086 13.5678C2.04229 14.2275 2.26584 14.7786 2.72758 15.2106C3.1893 15.6426 3.75403 15.8289 4.42107 15.9162C5.06266 16.0001 5.87318 16.0001 6.87657 16.0001H7.56246C8.56586 16.0001 9.37632 16.0001 10.018 15.9162C10.685 15.8289 11.2497 15.6426 11.7115 15.2106C12.1732 14.7786 12.3967 14.2275 12.5282 13.5678C12.6545 12.9332 12.7085 12.1245 12.7752 11.1233L13.1369 5.69757C13.1584 5.37499 12.9143 5.09606 12.5918 5.07456C12.2692 5.05306 11.9902 5.29712 11.9688 5.61969L11.6098 11.0043C11.5397 12.0564 11.4896 12.7883 11.38 13.339C11.2735 13.8733 11.125 14.156 10.9116 14.3557C10.6982 14.5553 10.4062 14.6847 9.86607 14.7554C9.30927 14.8282 8.57562 14.8294 7.52133 14.8294H6.9177C5.86342 14.8294 5.12972 14.8282 4.57292 14.7554C4.03282 14.6847 3.7408 14.5553 3.5274 14.3557C3.314 14.156 3.16545 13.8733 3.05903 13.339C2.94932 12.7883 2.89935 12.0564 2.82922 11.0043L2.47023 5.61969Z" fill="white" />
                                    <path d="M5.20978 7.02736C5.53147 6.9952 5.81828 7.2299 5.85051 7.55161L6.24076 11.4541C6.27291 11.7757 6.03822 12.0626 5.71651 12.0948C5.39484 12.1269 5.10797 11.8922 5.07581 11.5705L4.68557 7.66806C4.65339 7.34642 4.8881 7.05952 5.20978 7.02736Z" fill="white" />
                                    <path d="M9.75346 7.66806C9.78561 7.34642 9.55092 7.05952 9.22921 7.02736C8.90757 6.9952 8.62066 7.2299 8.5885 7.55161L8.19826 11.4541C8.1661 11.7757 8.4008 12.0626 8.72251 12.0948C9.04415 12.1269 9.33098 11.8922 9.36321 11.5705L9.75346 7.66806Z" fill="white" />
                                </svg>
                            </IconButton>
                        )}

                        <Tooltip title={t('customers.actions.gift_subscription', "Gift a subscription")} arrow>
                            <IconButton
                                size="small"
                                onClick={() =>
                                    setAssignDialog({
                                        open: true,
                                        customerId: params.row.id,
                                        customerName: params.row.name || "",
                                        planId: "",
                                    })
                                }
                                sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0', color: 'white' }}
                            >
                                <CardGiftcardIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {(params.row?.active_plan && !(params.row.active_plan.plan?.name?.toUpperCase() === 'FREE' && params.row.active_plan.plan?.durationDays === null)) && (
                            <Tooltip title={t('customers.actions.revoke', "Revoke")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        setRevokePermissionDialog({
                                            open: true,
                                            customerId: params.row.id,
                                            planId: "",
                                        })
                                    }
                                    sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0', color: 'white' }}
                                >
                                    <UndoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )
            },
        },
    ];

    const rows = useMemo(() => {
        return (customers || []).map((customer) => ({
            id: customer.id,
            name: customer.name || "N/A",
            email: customer.email,
            phone: customer.phone,
            isActive: customer.isActive,
            createdAt: customer.createdAt,
            profileImage: getFullUrl(customer.profileImage, "user") || null,
            active_plan: customer.subscription || null,
        }));
    }, [customers]);

    // if (loading) {
    //     return (
    //         <Box p={4} display="flex" justifyContent="center">
    //             <CircularProgress />
    //         </Box>
    //     );
    // }

    return (
        <Box sx={{ p: '36px 24px 24px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('customers.title', 'Listeners')}
                </Typography>

                <Box display="flex" gap={2}>
                    <TextField
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('search', 'Search')}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '999px',
                                backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                color: '#fff',
                                height: '38px',
                                '& fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                    borderWidth: '1px',
                                },
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: '10px 16px',
                                color: '#FF69B4',
                                fontSize: '15px',
                                lineHeight: '18px',

                            },
                            '& input::placeholder': {
                                color: 'rgba(255,255,255,0.6)',
                                opacity: 1,
                            },
                            '& .MuiFormHelperText-root': {
                                ml: '20px'
                            }
                        }}
                    />
                </Box>
            </Box>

            {/* Table */}
            <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>

                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50, 100]}
                    slots={{ toolbar: GridToolbar }}
                    paginationMode="server"
                    rowCount={pagination?.total || 0}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model);
                    }}
                    slotProps={{
                        toolbar: {
                            csvOptions: { disableToolbarButton: true }, // Removes the "Export as CSV" menu item
                            printOptions: { disableToolbarButton: true }, // Removes the "Print" menu item
                        },
                    }}
                    showToolbar
                    filterMode="server"
                    localeText={i18n.language?.toLowerCase().startsWith('fr') ? frFR.components.MuiDataGrid.defaultProps.localeText : enUS.components.MuiDataGrid.defaultProps.localeText}
                    sx={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '0',
                        '& .MuiDataGrid-toolbarContainer': {
                            padding: '12px',
                            margin: '0 -12px',
                            gap: '10px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        '& .MuiDataGrid-topContainer::after': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
                            color: '#fff',
                            border: '1px solid rgba(230, 210, 249, 0.2)',
                            borderRadius: '999px',
                            textTransform: 'none',
                            fontWeight: 500,
                            padding: '6px 12px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
                            width: '18px',
                            height: '18px',
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                            maxHeight: 'auto',
                            height: 'auto !important'
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            borderRadius: '0 !important',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone': {
                            position: 'inherit',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
                            maxHeight: 'none !important',
                            height: 'auto !important',
                        },
                        '& .MuiDataGrid-cell': {
                            height: 'auto !important',
                            minHeight: 'var(--height)',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                        },
                        '& .MuiDataGrid-cell[data-field="profileImage"]': {
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-cell>.MuiBox-root': {
                            minHeight: '100%',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
                            borderRadius: '0 !important',
                            borderColor: 'rgba(230, 210, 249, 0.2) !important',
                            color: '#fff',
                        },
                        '& .MuiDataGrid-columnHeaders [role=row]': {
                            backgroundColor: '#1a1a1a',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderColor: 'rgba(230, 210, 249, 0.2) !important'
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                            color: 'white',
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                            opacity: '0.8 !important',
                            fill: 'white',
                        },
                        '& .MuiDataGrid-cell .MuiChip-outlined': {
                            border: '0 !important',
                            height: 'auto',
                            fontWeight: '600',
                        },
                        '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
                            color: "#03c11c",
                        },
                        '& .MuiTablePagination-root': {
                            color: '#fff'
                        },
                        '& .MuiTablePagination-selectLabel': {
                            marginBottom: '0',
                        },
                        '& .MuiTablePagination-input': {
                            marginRight: '10px',
                        },
                        '& .MuiTablePagination-input .MuiSelect-select': {
                            paddingTop: '2px',
                            paddingBottom: '2px',
                            border: '1px solid rgba(230, 210, 249, 0.2)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            borderRadius: '4px',
                        },
                        '& .MuiTablePagination-input .MuiSvgIcon-root': {
                            fill: 'white'
                        },
                        '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
                            marginBottom: '0',
                        },
                        '& .MuiTablePagination-actions': {
                            marginLeft: '10px !important',
                        },
                        '& .MuiTablePagination-actions .MuiButtonBase-root': {
                            color: '#FF69B4',
                        },
                        '& .MuiDataGrid-filler': {
                            height: 'auto !important',
                        },
                        '& .MuiDataGrid-filler>div': {
                            borderColor: 'transparent',
                        },
                        '& .MuiDataGrid-overlayWrapper': {
                            height: 'auto !important',
                            maxHeight: '50px',
                        },
                        '& .MuiDataGrid-overlayWrapperInner': {
                            minHeight: '40px',
                            height: 'auto !important'
                        },
                        '& .MuiDataGrid-overlay': {
                            color: '#fff',
                            borderTop: '1px solid rgba(230, 210, 249, 0.2)',
                            backgroundColor: 'transparent',
                            minHeight: '40px',
                        },
                    }}
                />
            </Box>

            {/* Delete Customer  */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t("customers.popup_titles.confirm_delete", "Confirm Delete")}</DialogTitle>
                <DialogContent sx={{ pb: '0' }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{t("customers.messages.delete_customer_confirmation", "Are you sure you want to delete this customer?")}</Typography>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}> {t("cancel", "Cancel")} </Button>
                    <Button
                        variant="contained"
                        disabled={deleting}
                        onClick={handleDeleteConfirm}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >
                        {deleting ? <CircularProgress size={20} /> : t("delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>


            {/* Confirm toggle status  */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, id: null, newStatus: null })}>
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t("customers.popup_titles.confirm_status_update", "Confirm Status Update")}</DialogTitle>
                <DialogContent sx={{ pb: '0' }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{t("customers.messages.status_update_confirmation", "Are you sure you want to update status of this customer?")}</Typography>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    <Button onClick={() =>
                        setConfirmDialog({ open: false, id: null, newStatus: null })
                    }
                        sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}> {t("cancel", "Cancel")} </Button>
                    <Button
                        variant="contained"
                        disabled={statusUpdating}
                        onClick={handleConfirmStatusUpdate}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >
                        {statusUpdating ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t("customers.actions.update", "Update")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* assign subscription */}
            <Dialog open={assignDialog.open} onClose={() => setAssignDialog({ open: false, customerId: null, customerName: "", planId: "" })} fullWidth maxWidth="sm">
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
                    {t('customers.actions.gift_subscription', "Gift a subscription")}
                    <IconButton
                        onClick={() =>
                            setAssignDialog({ open: false, customerId: null, customerName: "", planId: "" })
                        }
                        disabled={loading || assigning} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        borderColor: 'rgba(230, 210, 249, 0.1)',
                        padding: '24px 16px',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(230, 210, 249, 0.3)',
                            borderRadius: '4px',
                            '&:hover': {
                                background: 'rgba(230, 210, 249, 0.5)',
                            },
                        },
                    }}
                >
                    <Box fullWidth>
                        <Select
                            name="planId"
                            displayEmpty
                            value={assignDialog.planId || ""}
                            onChange={(e) =>
                                setAssignDialog((prev) => ({
                                    ...prev,
                                    planId: e.target.value,
                                }))
                            }
                            sx={{
                                color: "#fff",
                                fontWeight: 400,
                                width: '100%',
                                borderRadius: "40px",
                                "& .MuiSelect-select": {
                                    padding: "12px 20px",
                                    borderRadius: "40px"
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "1px solid rgba(230, 210, 249, 0.1) !important",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "1px solid rgba(230, 210, 249, 0.1)",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    border: "1px solid rgba(230, 210, 249, 0.1)",
                                },
                                "& .MuiSvgIcon-root": {
                                    color: "#fff",
                                },
                            }}
                        >
                            <MenuItem value="">{t('customers.fields.select_plan', "Select Plan")}</MenuItem>

                            {Array.isArray(subscriptions) &&
                                subscriptions.map((plan) => {
                                    const durationDays = plan.durationDays ?? plan.durationInDays ?? plan.duration;
                                    const durationLabel = durationDays == null || durationDays === ""
                                        ? ""
                                        : ` - (${durationDays} ${t('days', 'Days')})`;

                                    return (
                                        <MenuItem key={plan.id} value={plan.id}>
                                            {`${plan.name || t('subscriptions.fields.plan', 'Plan')}${durationLabel}`}
                                        </MenuItem>
                                    );
                                })}
                        </Select>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
                    <Button
                        onClick={() =>
                            setAssignDialog({ open: false, customerId: null, customerName: "", planId: "" })
                        }
                        sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>
                        {t("cancel", "Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!assignDialog.planId || assigning}
                        onClick={handleAssignSubscription}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                            color: '#fff',
                            '&.Mui-disabled': {
                                opacity: 0.5,
                                color: '#fff',
                            }
                        }}
                    >
                        {assigning ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t("assign", "Assign")}
                    </Button>
                </DialogActions>

            </Dialog>

            {/* revoke subscription */}
            <Dialog open={revokePermissionDialog.open} onClose={() => setRevokePermissionDialog({ open: false, customerId: null, planId: "" })}>
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t("confirm_revoke", "Confirm Revoke Subscription")}</DialogTitle>
                <DialogContent sx={{ pb: '0' }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{t("revoke_subscription_confirmation", "Are you sure you want to revoke the subscription?")}</Typography>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    <Button onClick={() => setRevokePermissionDialog({ open: false, customerId: null, planId: "" })} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}> {t("cancel", "Cancel")} </Button>
                    <Button
                        variant="contained"
                        disabled={!revokePermissionDialog.customerId || revoking}
                        onClick={handleRevokeSubscription}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >
                        {revoking ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t("revoke", "Revoke")}
                    </Button>
                </DialogActions>
            </Dialog>

            <GlobalStyles
                styles={{
                    '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
                        backgroundColor: '#252525 !important',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        fontSize: '15px',
                        color: '#fff',
                        borderRadius: '4px',
                    },
                    '.MuiDialog-root .MuiPaper-root': {
                        borderRadius: '20px',
                    },
                    '.MuiDataGrid-columnsManagementHeader': {
                        padding: '10px !important',
                    },
                    '.MuiInputBase-formControl': {
                        fontSize: '14px !important',
                        color: 'white !important',
                        borderRadius: '999px !important',
                    },
                    '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
                        borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                        padding: '4px 8px 4px 16px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement': {
                        padding: '0px 16px 12px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
                        color: '#FF69B4',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
                        color: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
                        borderColor: 'rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
                        display: 'none',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
                        padding: '0',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
                        padding: '5px 10px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: '#FF69B4',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
                        borderColor: 'rgba(230, 210, 249, 0.3) !important',
                        margin: '0',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        minWidth: '32px',
                    },
                    '.MuiDataGrid-scrollbarFiller': {
                        borderTop: 'none !important',
                    },
                    '& .MuiDataGrid-scrollbar': {
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(230, 210, 249, 0.3)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: 'rgba(230, 210, 249, 0.5)',
                        },
                    },
                }}
            />
        </Box>
    );


}

export default CustomerList;
